/**
 * Brand strings — app name, tagline, description.
 *
 * SSOT for the branding literals: every render site (page titles, the header
 * logo, marketing copy, transactional emails) reads them from here, so a second
 * instance (`fly.yashaka.toml`) rebrands with env alone — no code change.
 *
 * Env vars, all optional, blank counts as unset: `APP_NAME`, `APP_TAGLINE`,
 * `APP_DESCRIPTION` (schema: `app/utils/env.server.ts`). They are PUBLIC values
 * — whitelisted into the client bundle by `getEnv()`, same as `ALLOW_INDEXING`.
 *
 * Read lazily (function calls), never at module scope: on the server
 * `global.ENV` is assigned in `entry.server.tsx` AFTER the module graph is
 * imported, so the server reads `process.env`; the browser has no `process` and
 * reads `window.ENV`, injected by `root.tsx` before the module bundle runs.
 */

export const DEFAULT_APP_NAME = 'Чорноморець творить'

export const DEFAULT_APP_DESCRIPTION = 'CRM+LMS школи Петра Чорноморця'

/**
 * The name is a brand and is NOT translated (root.tsx § Logo); the tagline is —
 * hence a per-locale default.
 */
export const DEFAULT_APP_TAGLINE = {
	uk: 'Школа Петра Чорноморця — курси, учасники, оплати',
	en: 'Petro Chornomorets school — courses, participants, payments',
} as const

export type BrandLocale = keyof typeof DEFAULT_APP_TAGLINE

type BrandEnvKey = 'APP_NAME' | 'APP_TAGLINE' | 'APP_DESCRIPTION'

function fromEnv(key: BrandEnvKey): string | undefined {
	const raw =
		(typeof process !== 'undefined' ? process.env?.[key] : undefined) ??
		(typeof window !== 'undefined' ? window.ENV?.[key] : undefined)
	// a blank override is an unset override — never a blank brand on screen;
	// surrounding whitespace is equally non-meaningful, so return the trimmed
	// value (a padded APP_NAME must not shift the logo-line split)
	const trimmed = raw?.trim()
	return trimmed ? trimmed : undefined
}

export function getAppName(): string {
	return fromEnv('APP_NAME') ?? DEFAULT_APP_NAME
}

export function getAppDescription(): string {
	return fromEnv('APP_DESCRIPTION') ?? DEFAULT_APP_DESCRIPTION
}

/**
 * An `APP_TAGLINE` override applies to every locale (one knob per instance); a
 * per-locale override can be added if a second instance ever needs one.
 */
export function getAppTagline(locale: BrandLocale): string {
	return fromEnv('APP_TAGLINE') ?? DEFAULT_APP_TAGLINE[locale]
}

/**
 * The header logo renders the name on two lines — first word light, the rest
 * bold («Чорноморець» / «творить»). The split rule lives with the name so a
 * renamed instance keeps the same visual treatment; a single-word name yields
 * an empty `rest`, and the caller renders only the first line.
 */
export function getAppNameLines(): { first: string; rest: string } {
	const [first = '', ...tail] = getAppName().split(/\s+/)
	return { first, rest: tail.join(' ') }
}
